// Shared utility functions

/**
 * Generate URL-friendly slug from text
 */
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
}

/**
 * Generate author slug from display name (consistent with user service)
 */
export function generateAuthorSlug(displayName: string): string {
  return generateSlug(displayName);
}

/**
 * Author link target. Per-author pages were retired (F6) — `/authors/*`
 * 308-redirects to `/read`. Link straight at the live hub rather than
 * advertising a redirect. (displayName kept for call-site compatibility.)
 */
export function getAuthorUrl(_displayName: string): string {
  return '/read';
}

/**
 * Calculate estimated read time for content (improved markdown handling)
 */
export function calculateReadTime(content: string): number {
  const wordsPerMinute = 200; // Average reading speed for technical content
  
  // Clean markdown syntax to get actual readable text
  const cleanedContent = content
    // Remove code blocks (```...```)
    .replace(/```[\s\S]*?```/g, '')
    // Remove inline code (`...`)
    .replace(/`[^`]*`/g, '')
    // Remove markdown links but keep the text [text](url) -> text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // Remove markdown images ![alt](url)
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    // Remove markdown headers (#, ##, ###, etc.)
    .replace(/^#{1,6}\s+/gm, '')
    // Remove markdown emphasis (**bold**, *italic*, __bold__, _italic_)
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    // Remove markdown strikethrough (~~text~~)
    .replace(/~~(.*?)~~/g, '$1')
    // Remove markdown blockquotes (> text)
    .replace(/^>\s+/gm, '')
    // Remove markdown lists (-, *, +, 1., etc.)
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    // Remove horizontal rules (---, ***, ___)
    .replace(/^[\s]*[-*_]{3,}[\s]*$/gm, '')
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove extra whitespace and normalize
    .replace(/\s+/g, ' ')
    .trim();
  
  // Count words (split by whitespace and filter empty strings)
  const words = cleanedContent.split(/\s+/).filter(word => word.length > 0);
  const wordCount = words.length;
  
  // Calculate base read time
  let readTime = Math.ceil(wordCount / wordsPerMinute);
  
  // Add time for code blocks (people read code slower)
  const codeBlocks = (content.match(/```[\s\S]*?```/g) || []).length;
  const inlineCode = (content.match(/`[^`]+`/g) || []).length;
  readTime += Math.ceil(codeBlocks * 0.5 + inlineCode * 0.1); // Extra time for code
  
  // Add time for images (people pause to look at images)
  const images = (content.match(/!\[[^\]]*\]\([^)]*\)/g) || []).length;
  readTime += Math.ceil(images * 0.2); // 12 seconds per image
  
  // Minimum 1 minute read time
  return Math.max(1, readTime);
}

// Date formatting utilities have been consolidated into @/utils/date-formatting
// Import from there: formatDate, formatDateTime, formatRelativeDate, formatBreakingNewsDate, etc.
